import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import styled from 'styled-components';

import { Input } from '~/atomic/atom/Input';
import { PhoneInput } from '~/atomic/molecula/PhoneInput';
import { TextSmallStyle } from '~/atomic/Typography';

const isPhoneStart = (value: string) => /^[+78]/.test(value);

const NON_RUSSIAN_PHONE_WARNING = 'Принимаются только российские номера (+7)';

interface ReceiptContactInputProps {
  value: string;
  onChange: (value: string) => void;
  onPressEnter?: () => void;
  error?: string;
  autoFocus?: boolean;
}

export const ReceiptContactInput = ({
  value,
  onChange,
  onPressEnter,
  error,
  autoFocus,
}: ReceiptContactInputProps) => {
  const isPhone = isPhoneStart(value);
  const phoneRef = useRef<HTMLInputElement>(null);
  const prevIsPhoneRef = useRef(isPhone);

  const isNonRussianPhone = useMemo(() => {
    if (!isPhone) return false;
    const digits = value.replace(/\D/g, '');
    return digits.length >= 2 && !digits.startsWith('7');
  }, [isPhone, value]);

  const displayError = isNonRussianPhone ? NON_RUSSIAN_PHONE_WARNING : error;
  const hasError = !!displayError;

  useEffect(() => {
    if (isPhone && !prevIsPhoneRef.current && phoneRef.current) {
      phoneRef.current.focus();
    }
    prevIsPhoneRef.current = isPhone;
  }, [isPhone]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '8') {
      onChange('+7');
      return;
    }
    onChange(raw);
  }, [onChange]);

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      onChange('');
      return;
    }
    onChange(raw);
  }, [onChange]);

  const handlePhoneKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const cursorPos = (e.target as HTMLInputElement).selectionStart || 0;
    if (
      (e.key === 'Backspace' || e.key === 'Delete')
      && cursorPos <= 1
    ) {
      e.preventDefault();
      onChange('');
    }
    if (e.key === 'Enter' && onPressEnter) {
      onPressEnter();
    }
  }, [onChange, onPressEnter]);

  if (isPhone) {
    return (
      <>
        <PhoneInput
          inputMode="tel"
          guide={false}
          mask={false}
          name="receiptContact"
          value={value}
          placeholder="+7 (000) 000-0000"
          onChange={handlePhoneChange}
          onKeyDown={handlePhoneKeyDown}
          render={(ref, props) => (
            <SCPhoneInput
              ref={(el) => {
                ref(el);
                (phoneRef as React.MutableRefObject<HTMLInputElement>).current = el;
              }}
              $hasError={hasError}
              {...props}
            />
          )}
        />
        {displayError ? (
          <SCError>{displayError}</SCError>
        ) : null}
      </>
    );
  }

  return (
    <>
      <SCEmailInput
        name="receiptContact"
        onChange={handleEmailChange}
        onPressEnter={onPressEnter}
        value={value}
        placeholder="E-mail или +7..."
        autoFocus={autoFocus}
        status={error ? 'error' : undefined}
      />
      {error ? (
        <SCError>{error}</SCError>
      ) : null}
    </>
  );
};

const SCEmailInput = styled(Input)`
  width: 100%;
  height: 48px;
  font-size: 16px;
`;

const SCPhoneInput = styled.input<{ $hasError?: boolean }>`
  box-sizing: border-box;
  margin: 0;
  width: 100%;
  padding: 4px 11px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 16px;
  line-height: 1.5715;
  background-color: #fff;
  background-image: none;
  border: 1px solid ${({ $hasError }) => ($hasError ? 'var(--error-color)' : '#d9d9d9')};
  border-radius: 2px;
  transition: all 0.3s;
  height: 48px;

  &:hover,
  &:active,
  &:focus {
    border-color: ${({ $hasError }) => ($hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    outline: none;
  }
`;

const SCError = styled.span`
  ${TextSmallStyle};
  color: var(--error-color);
  margin-top: 6px;
`;
